import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { SnackbarNotice } from "@/shared/notifications/SnackbarNotice";
import { TextField, Button, Typography } from "@mui/material";
import { signIn } from "aws-amplify/auth";
import { apiClient } from "@/shared/api/client";
import { useAuth } from "@/app/context/AuthContext";
import {
  AuthCard,
  AuthPasswordField,
  authFeedbackSx,
  authFooterSx,
  authLinkSx,
  authMutedTextSx,
  authFieldSx,
  authPrimaryActionSx,
} from "./components";
import { authErrorCopy, LOGIN_FAILURE_COPY } from "./auth-copy";
import { safeReturnPath } from "./auth-navigation";

export const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refetch } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await signIn({ username: email, password });

      if (result.isSignedIn) {
        // Check if user has completed onboarding (has a player profile)
        const me = await apiClient<{ userId: string; email: string; profile: unknown }>("/auth/me");

        // Refetch auth context to update isAuthenticated state
        await refetch();

        void navigate(safeReturnPath(`?${searchParams}`) ?? (me.profile ? "/home" : "/profile"));
      } else if (result.nextStep.signInStep === "CONFIRM_SIGN_UP") {
        setError(LOGIN_FAILURE_COPY);
      } else {
        setError("Sign in requires additional steps. Please try again.");
      }
    } catch (err: unknown) {
      setError(
        err instanceof Error && err.name === "UserNotConfirmedException" ? LOGIN_FAILURE_COPY : authErrorCopy(err),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthCard title="WELCOME BACK" detail="Continue from your latest games, notes, and verified coaching evidence.">
      <form onSubmit={(event) => void handleSubmit(event)}>
        <TextField
          autoComplete="email"
          autoCapitalize="none"
          label="Email"
          name="email"
          spellCheck={false}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          fullWidth
          sx={authFieldSx}
        />
        <AuthPasswordField
          autoComplete="current-password"
          label="Password"
          name="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          fullWidth
        />
        {error && (
          <SnackbarNotice severity="error" sx={authFeedbackSx}>
            {error}
          </SnackbarNotice>
        )}
        <Button type="submit" variant="contained" fullWidth disabled={isSubmitting} sx={authPrimaryActionSx}>
          {isSubmitting ? "LOGGING IN…" : "LOG IN"}
        </Button>
      </form>
      <Typography variant="caption" sx={{ ...authMutedTextSx, display: "block", mt: 2 }}>
        <Link to="/reset-password" style={authLinkSx}>
          FORGOT PASSWORD?
        </Link>
      </Typography>
      <Typography variant="caption" sx={{ ...authMutedTextSx, display: "block", mt: 1.5 }}>
        <Link to="/signup?confirm=1" style={authLinkSx}>
          CONFIRM AN EXISTING ACCOUNT
        </Link>
      </Typography>
      <Typography variant="caption" sx={authFooterSx}>
        No account?{" "}
        <Link to="/signup" style={authLinkSx}>
          CREATE ONE
        </Link>
      </Typography>
    </AuthCard>
  );
};
